import React, { useEffect, useState } from 'react';
import {
    Button, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Select, MenuItem, OutlinedInput, FormControl, FormHelperText, Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import useFormularioInscricao from './useFormularioInscricao';
import ContainedButton from '../../../shared/buttons/ContainedButton';
import TextButton from '../../../shared/buttons/TextButton';
import Close from '@material-ui/icons/CloseOutlined';
import Save from '@material-ui/icons/SaveOutlined';
import Add from '@material-ui/icons/AddOutlined';
import Delete from '@material-ui/icons/DeleteOutlined';
import Edit from '@material-ui/icons/EditOutlined';
import * as Yup from 'yup';
import LabelValue from "../../../shared/labelvalue/LabelValue.js";
import DataGrid from '../../../shared/datagrid/DataGrid.tsx';
import useConfirmationDialog from "../../../shared/confirmationdialog/useConfirmationDialog";
import { containedButton } from "../../../../utils/styles";
import { validateCPF } from "../../../../utils/index.js";
import {
    CpfNumberFormatCustom,
    RgNumberFormatCustom,
    DataNascimentoNumberFormatCustom
} from "../../../shared/formatters";
import DialogRenda from './DialogRenda.jsx';
import { criarColunaAcoes } from './AcoesLinha.jsx';
import { descricaoTipoRenda, formatarMoeda, rendasDoMembro, somarRendas } from './rendaMembro';

const useStyles = makeStyles(theme => ({
    dialogContent: {
        paddingTop: theme.spacing(2)
    },
    columnSmall: {
        width: '100px'
    },
    totalRendas: {
        marginTop: theme.spacing(1),
        textAlign: 'right',
        [theme.breakpoints.down('xs')]: {
            textAlign: 'left'
        }
    },
    ...containedButton(theme, false, false)
}));

const validationSchema = Yup.object().shape({
    descricao: Yup.string().required('Campo obrigatório'),
    grauParentescoId: Yup.number().typeError('Campo obrigatório').required('Campo obrigatório'),
    enderecoComposicaoFamiliar: Yup.object().nullable().required('Campo obrigatório'),
    rendas: Yup.array().nullable(),
    cpf: Yup.string().required('Campo obrigatório')
        .test('cpf-valido', 'CPF inválido', valor => !valor || (valor.replace(/\D/g, '').length === 11 && validateCPF(valor.replace(/\D/g, '')))),
    rg: Yup.string().nullable(),
    dataNascimento: Yup.string().required('Campo obrigatório'),
    declaracaoIR: Yup.string().nullable()
});

export default function DialogMembroFamiliar({ open, onClose, onSave, initialValues, enderecos = [], tiposEndereco = [], grausParentesco = [] }) {
    const classes = useStyles();
    const showConfirmation = useConfirmationDialog();
    const [rendaAberta, setRendaAberta] = useState(false);
    const [indiceRendaEmEdicao, setIndiceRendaEmEdicao] = useState(-1);

    const formDialogo = useFormularioInscricao({
        valoresIniciais: {
            descricao: '',
            rendas: [],
            grauParentescoId: '',
            enderecoComposicaoFamiliar: null,
            cpf: '',
            rg: '',
            dataNascimento: '',
            declaracaoIR: 'N'
        },
        schema: validationSchema,
        aoSubmeter: (values) => {
            onSave(values);
        }
    });

    const getTipoEnderecoDescricao = (tipo) => {
        if (!tipo) return '';
        if (typeof tipo === 'object') {
            return tipo.descricao || '';
        }
        const found = tiposEndereco.find(t => t.nome === tipo);
        return found ? found.descricao : tipo;
    };

    const findEnderecoIndex = (enderecoSelecionado) => {
        if (!enderecoSelecionado || !enderecos || enderecos.length === 0) {
            return -1;
        }

        // 1. Busca por ID com coerção de tipo para String
        if (enderecoSelecionado.id != null) {
            const idxById = enderecos.findIndex(addr => addr?.id != null && String(addr.id) === String(enderecoSelecionado.id));
            if (idxById > -1) {
                return idxById;
            }
        }

        // 2. Busca por referência de objeto
        const idxByRef = enderecos.indexOf(enderecoSelecionado);
        if (idxByRef > -1) {
            return idxByRef;
        }

        // 3. Busca por equivalência de propriedades
        const cleanStr = (s) => (s ? String(s).trim().toLowerCase() : '');
        const cleanNum = (s) => (s ? String(s).replace(/\D/g, '') : '');

        const idxByFields = enderecos.findIndex(addr => {
            if (!addr) return false;

            const sameTipo = cleanStr(addr.tipoEndereco) === cleanStr(enderecoSelecionado.tipoEndereco);
            const sameLogradouro = cleanStr(addr.logradouro) === cleanStr(enderecoSelecionado.logradouro);
            const sameNumero = cleanStr(addr.numero) === cleanStr(enderecoSelecionado.numero);
            const sameCep = cleanNum(addr.cep) === cleanNum(enderecoSelecionado.cep);

            if (sameLogradouro && sameNumero) return true;
            if (sameTipo && (sameLogradouro || sameCep)) return true;
            if (sameTipo && enderecos.length === 1) return true;
            return false;
        });

        return idxByFields;
    };

    const getEnderecoSelectValue = (enderecoSelecionado) => {
        if (!enderecoSelecionado) {
            return '';
        }

        const idx = findEnderecoIndex(enderecoSelecionado);
        if (idx > -1) {
            const addr = enderecos[idx];
            return addr?.id != null ? String(addr.id) : `idx-${idx}`;
        }

        if (enderecoSelecionado.id != null) {
            return String(enderecoSelecionado.id);
        }

        return '';
    };

    // --- Ocupações e rendas do integrante ---
    const rendas = formDialogo.valores.rendas || [];

    const handleAddRendaMembro = () => {
        setIndiceRendaEmEdicao(-1);
        setRendaAberta(true);
    };

    const handleEditRendaMembro = (indice) => {
        setIndiceRendaEmEdicao(indice);
        setRendaAberta(true);
    };

    const handleSaveRendaMembro = (form) => {
        const atual = [...rendas];
        const item = {
            ...(indiceRendaEmEdicao > -1 ? atual[indiceRendaEmEdicao] : {}),
            tpRenda: form.tpRenda,
            ocupacao: String(form.ocupacao || '').trim(),
            vlrRenda: parseFloat(form.vlrRenda) || 0
        };
        if (indiceRendaEmEdicao > -1) {
            atual[indiceRendaEmEdicao] = item;
        } else {
            atual.push(item);
        }
        formDialogo.definirValor('rendas', atual);
        setRendaAberta(false);
    };

    const handleDeleteRendaMembro = (indice) => {
        showConfirmation('Deseja realmente remover esta ocupação?', () => {
            const atual = [...rendas];
            atual.splice(indice, 1);
            formDialogo.definirValor('rendas', atual);
        });
    };

    const rendaColumns = [
        { label: 'Tipo', field: 'tpRenda', valueGetter: (val) => descricaoTipoRenda(val) },
        { label: 'Ocupação', field: 'ocupacao' },
        { label: 'Valor', field: 'vlrRenda', valueGetter: (val) => formatarMoeda(val) },
        criarColunaAcoes(row => {
            const indice = rendas.indexOf(row);
            return [
                { titulo: 'Editar', icone: <Edit fontSize="small" />, onClick: () => handleEditRendaMembro(indice) },
                { titulo: 'Remover', icone: <Delete fontSize="small" />, onClick: () => handleDeleteRendaMembro(indice) }
            ];
        }, { className: classes.columnSmall })
    ];

    useEffect(() => {
        if (open) {
            const defaultEndereco = (enderecos || []).find(a => a.tipoEndereco === 'RESIDENCIAL') || (enderecos || [])[0] || null;
            
            let enderecoInicial = null;
            if (initialValues) {
                const endProp = initialValues.enderecoComposicaoFamiliar || initialValues.endereco;
                if (endProp) {
                    const idx = findEnderecoIndex(endProp);
                    enderecoInicial = idx > -1 ? enderecos[idx] : endProp;
                } else {
                    enderecoInicial = defaultEndereco;
                }
            } else {
                enderecoInicial = defaultEndereco;
            }

            const mappedValues = initialValues ? {
                descricao: initialValues.descricao || '',
                rendas: rendasDoMembro(initialValues).map(renda => ({ ...renda })),
                grauParentescoId: initialValues.grauParentesco?.id ?? '',
                enderecoComposicaoFamiliar: enderecoInicial,
                cpf: initialValues.cpf || '',
                rg: initialValues.rg || '',
                dataNascimento: initialValues.dataNascimento || '',
                declaracaoIR: initialValues.declaracaoIR || 'N'
            } : {
                descricao: '',
                rendas: [],
                grauParentescoId: '',
                enderecoComposicaoFamiliar: defaultEndereco,
                cpf: '',
                rg: '',
                dataNascimento: '',
                declaracaoIR: 'N'
            };
            formDialogo.reiniciar({
                values: mappedValues
            });
        }
    }, [open, initialValues, enderecos]);

    return (
        <Dialog scroll="body" open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{initialValues && initialValues.descricao ? 'Editar Integrante' : 'Adicionar Integrante'}</DialogTitle>
            <DialogContent className={classes.dialogContent}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <LabelValue label="Nome Completo *">
                            <FormControl variant="outlined" fullWidth error={formDialogo.tocados.descricao && !!formDialogo.erros.descricao}>
                                <OutlinedInput
                                    name="descricao"
                                    value={formDialogo.valores.descricao}
                                    onChange={formDialogo.aoMudar}
                                    onBlur={formDialogo.aoSair}
                                />
                                {formDialogo.tocados.descricao && formDialogo.erros.descricao && (
                                    <FormHelperText>{formDialogo.erros.descricao}</FormHelperText>
                                )}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12}>
                        <LabelValue label="Grau de Parentesco *">
                            <FormControl variant="outlined" fullWidth error={formDialogo.tocados.grauParentescoId && !!formDialogo.erros.grauParentescoId}>
                                <Select
                                    name="grauParentescoId"
                                    value={formDialogo.valores.grauParentescoId}
                                    onChange={formDialogo.aoMudar}
                                    onBlur={formDialogo.aoSair}
                                >
                                    <MenuItem value="">Selecione...</MenuItem>
                                    {grausParentesco.map(item => (
                                        <MenuItem key={item.id} value={item.id}>{item.descricao}</MenuItem>
                                    ))}
                                </Select>
                                {formDialogo.tocados.grauParentescoId && formDialogo.erros.grauParentescoId && (
                                    <FormHelperText>{formDialogo.erros.grauParentescoId}</FormHelperText>
                                )}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12}>
                        <LabelValue label="Endereço *">
                            <FormControl variant="outlined" fullWidth error={formDialogo.tocados.enderecoComposicaoFamiliar && !!formDialogo.erros.enderecoComposicaoFamiliar}>
                                <Select
                                    name="enderecoComposicaoFamiliar"
                                    value={getEnderecoSelectValue(formDialogo.valores.enderecoComposicaoFamiliar)}
                                    onChange={(e) => {
                                        const optionValue = e.target.value;
                                        if (!optionValue) {
                                            formDialogo.definirValor('enderecoComposicaoFamiliar', null);
                                            return;
                                        }

                                        if (String(optionValue).startsWith('idx-')) {
                                            const idx = Number(String(optionValue).replace('idx-', ''));
                                            formDialogo.definirValor('enderecoComposicaoFamiliar', enderecos[idx] || null);
                                            return;
                                        }

                                        const enderecoPorId = enderecos.find(addr => String(addr?.id) === String(optionValue));
                                        formDialogo.definirValor('enderecoComposicaoFamiliar', enderecoPorId || null);
                                    }}
                                    onBlur={formDialogo.aoSair}
                                >
                                    <MenuItem value="">Selecione...</MenuItem>
                                    {enderecos.map((addr, idx) => (
                                        <MenuItem key={addr?.id ?? `idx-${idx}`} value={addr?.id != null ? String(addr.id) : `idx-${idx}`}>
                                            {getTipoEnderecoDescricao(addr.tipoEndereco)} - {addr.logradouro}, {addr.numero}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {formDialogo.tocados.enderecoComposicaoFamiliar && formDialogo.erros.enderecoComposicaoFamiliar && (
                                    <FormHelperText>{formDialogo.erros.enderecoComposicaoFamiliar}</FormHelperText>
                                )}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LabelValue label="CPF *">
                            <FormControl variant="outlined" fullWidth error={formDialogo.tocados.cpf && !!formDialogo.erros.cpf}>
                                <OutlinedInput
                                    name="cpf"
                                    value={formDialogo.valores.cpf}
                                    onChange={formDialogo.aoMudar}
                                    onBlur={formDialogo.aoSair}
                                    inputComponent={CpfNumberFormatCustom}
                                />
                                {formDialogo.tocados.cpf && formDialogo.erros.cpf && (
                                    <FormHelperText>{formDialogo.erros.cpf}</FormHelperText>
                                )}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LabelValue label="RG">
                            <FormControl variant="outlined" fullWidth error={formDialogo.tocados.rg && !!formDialogo.erros.rg}>
                                <OutlinedInput
                                    name="rg"
                                    value={formDialogo.valores.rg}
                                    onChange={formDialogo.aoMudar}
                                    onBlur={formDialogo.aoSair}
                                    inputComponent={RgNumberFormatCustom}
                                />
                                {formDialogo.tocados.rg && formDialogo.erros.rg && (
                                    <FormHelperText>{formDialogo.erros.rg}</FormHelperText>
                                )}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LabelValue label="Data de Nascimento *">
                            <FormControl variant="outlined" fullWidth error={formDialogo.tocados.dataNascimento && !!formDialogo.erros.dataNascimento}>
                                <OutlinedInput
                                    name="dataNascimento"
                                    value={formDialogo.valores.dataNascimento}
                                    onChange={formDialogo.aoMudar}
                                    onBlur={formDialogo.aoSair}
                                    inputComponent={DataNascimentoNumberFormatCustom}
                                />
                                {formDialogo.tocados.dataNascimento && formDialogo.erros.dataNascimento && (
                                    <FormHelperText>{formDialogo.erros.dataNascimento}</FormHelperText>
                                )}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LabelValue label="Declaração Imposto de Renda">
                            <FormControl variant="outlined" fullWidth error={formDialogo.tocados.declaracaoIR && !!formDialogo.erros.declaracaoIR}>
                                <Select
                                    name="declaracaoIR"
                                    value={formDialogo.valores.declaracaoIR}
                                    onChange={formDialogo.aoMudar}
                                    onBlur={formDialogo.aoSair}
                                >
                                    <MenuItem value="S">Sim</MenuItem>
                                    <MenuItem value="N">Não</MenuItem>
                                    <MenuItem value="I">Isento</MenuItem>
                                </Select>
                                {formDialogo.tocados.declaracaoIR && formDialogo.erros.declaracaoIR && (
                                    <FormHelperText>{formDialogo.erros.declaracaoIR}</FormHelperText>
                                )}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12}>
                        <LabelValue label="Ocupações e rendas">
                            <DataGrid
                                responsive="card"
                                toolbarContent={(
                                    <Button
                                        variant="contained"
                                        className={classes.containedButton}
                                        startIcon={<Add />}
                                        onClick={handleAddRendaMembro}
                                    >
                                        Adicionar
                                    </Button>
                                )}
                                tableContainerProps={{
                                    component: 'div'
                                }}
                                rows={rendas}
                                columns={rendaColumns}
                                comparatorKeys={['tpRenda', 'ocupacao']}
                                noRecordsText="Nenhuma ocupação informada. Se o integrante não tem renda, pode deixar em branco."
                            />
                            <Typography variant="body2" className={classes.totalRendas}>
                                Renda bruta mensal total: <strong>{formatarMoeda(somarRendas(rendas))}</strong>
                            </Typography>
                        </LabelValue>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogRenda
                open={rendaAberta}
                onClose={() => setRendaAberta(false)}
                onSave={handleSaveRendaMembro}
                initialValues={indiceRendaEmEdicao > -1 ? rendas[indiceRendaEmEdicao] : null}
                titulo="Ocupação/Renda"
            />
            <DialogActions>
                <TextButton onClick={onClose} startIcon={<Close />}>Fechar</TextButton>
                <ContainedButton onClick={formDialogo.submeter} startIcon={<Save />} disabled={formDialogo.enviando}>
                    Salvar
                </ContainedButton>
            </DialogActions>
        </Dialog>
    );
}
